import { PlusIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { type Control, type FieldPath, useController, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { FormMessage } from "../../../../../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { DateInput } from "../../../../../components/ui/uikit/dateInput";
import { Input } from "../../../../../components/ui/uikit/input";
import { expandYearText } from "../../../../../lib/twoDigitYear";
import { cn } from "../../../../../lib/utils";
import { applyDigitFormatWithCaret } from "../caretPreservingDigitFormat";
import {
  ADDRESS_APARTMENT_NUMBER_MAX_LENGTH,
  ADDRESS_STREET_NAME_MAX_LENGTH,
  ADDRESS_STREET_NUMBER_MAX_LENGTH,
  APPLICANT_SSN_TYPES,
  type ApplicantSsnType,
  CITY_OPTIONS,
  formatZip,
  MAX_PHONES_PER_PERSON,
  RESIDENCE_OWNER_TYPE_OPTIONS,
  RESIDING_SINCE_YEAR_MAX_LENGTH,
  SHORT_MONTH_OPTIONS,
  SUFFIX_OPTIONS,
  US_STATE_OPTIONS,
  ZIP_PASTE_MAX_LENGTH,
} from "../constants";
import { useFormReadOnly } from "../readOnlyContext";
import { getSurfacedSelect } from "../selectSurfacing";
import type { CustomerInfoInput, SignersInput } from "../Signers.schema";
import {
  DUPLICATE_PHONE_MESSAGE,
  EMAIL_INVALID_MESSAGE,
  findDuplicatePhoneIndexes,
  isValidEmailFormat,
  makeEmptyPhone,
} from "../Signers.schema";
import { PhoneRow } from "./PhoneRow";
import { SsnInput } from "./SsnInput";
import { SubsectionHeader } from "./SubsectionHeader";
import { useMirrorAddress } from "./useMirrorAddress";
import { useZipAutofill } from "./useZipAutofill";
import { ZipLookupMessage } from "./ZipLookupMessage";

// ---------------------------------------------------------------------------
// Radix Select sentinel — Radix requires a non-empty string value; map "" ↔ this
// ---------------------------------------------------------------------------
const NONE_SENTINEL = "__none__";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface CustomerInformationProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  /** Submit checklist verdict for this signer's residence (STP6DRRES / STP6SSRES). */
  hasChecklistWarning?: boolean;
  /** Applicant SSN/ITIN status — hides the SSN/ITIN field for a no-SSN/ITIN applicant. */
  applicantSsnType: ApplicantSsnType;
  /**
   * Locks SSN/ITIN, Date of Birth, and the Name row (First/Middle/Last/Suffix)
   * independent of the rest of the form — matrix §4.3 (`isStep6ApplicantInfoDisable`)
   * OR'd with the pre-approval `disablePreApp` override (step6-status-plan.md
   * Phase 1): these fields stay locked even in states where the surrounding
   * Residence/Bank/Income fields are editable.
   */
  identityReadOnly: boolean;
  /** Ref callback registering the whole Customer Information block (the "Residence" section) with the Signers-level scroll-spy. */
  residenceSectionRef?: (element: HTMLElement | null) => (() => void) | void;
  /** Deal / approval id, threaded down to the Primary phone row's Call Script modal. */
  approvalId?: string;
  /** Real applicant id for this person, threaded down to the Primary phone row's Call Script modal. */
  applicantId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getCustomerFieldPath(personPath: PersonPath, field: keyof CustomerInfoInput): FieldPath<SignersInput> {
  return `${personPath}.customerInfo.${field}` as FieldPath<SignersInput>;
}

// ---------------------------------------------------------------------------
// Required marker — visual only (no validation enforcement in this story)
// ---------------------------------------------------------------------------
function RequiredMarker() {
  return (
    <span className="text-destructive ml-0.5" aria-hidden="true">
      *
    </span>
  );
}

// ---------------------------------------------------------------------------
// SsnField — labelled 3-part SSN/ITIN split input wired to RHF via useController.
// Stores a canonical digits string; validation lives in customerInfoSchema.
// ---------------------------------------------------------------------------
interface SsnFieldProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  /**
   * Lock the field. Always enforced, including while the value carries a
   * driver/2nd-signer duplicate-SSN error from signersSchema — that case is
   * unfixable here by design and must be corrected upstream (CAPS / credit
   * application), not edited in this UI.
   */
  disabled?: boolean;
}

function SsnField({ control, personPath, disabled }: SsnFieldProperties) {
  const { t } = useTranslation();
  const uid = useId();
  const { field, fieldState } = useController({
    control,
    name: getCustomerFieldPath(personPath, "ssnOrItin"),
  });
  const label = t("review.customer.personal.ssnOrItin");
  const errorId = `${uid}-ssn-error`;

  return (
    <div className="flex w-fit flex-col gap-1">
      <label className="text-xs font-medium">
        {label}
        <RequiredMarker />
      </label>
      <SsnInput
        idBase={`${uid}-ssn`}
        value={field.value == null ? "" : String(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        ariaLabel={label}
        required
        invalid={Boolean(fieldState.error)}
        disabled={disabled}
      />
      <FormMessage error={fieldState.error} id={errorId} className="mt-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleTextField — a labelled text input wired to RHF via useController
// ---------------------------------------------------------------------------
interface SimpleTextFieldProperties {
  control: Control<SignersInput>;
  fieldPath: FieldPath<SignersInput>;
  label: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Hide the visible label (label is exposed via aria-label instead). Used by grouped fields like Name. */
  hideLabel?: boolean;
  /** Pass "off" for PII inputs (SSN/ITIN, DOB) to prevent browser caching. */
  autoComplete?: string;
  /** Force the invalid (red-border) state independent of RHF validation — e.g. a failed ZIP lookup. */
  invalid?: boolean;
  /** Transforms the raw typed/pasted value before it reaches RHF (e.g. ZIP+4 dash formatting). */
  format?: (value: string) => string;
  /** Transforms the value on blur/tab-out (e.g. expanding a 2-digit year). */
  blurFormat?: (value: string) => string;
  maxLength?: number;
  /** Uppercases the value on blur/tab-out — matches dealer-ui behavior for address fields. */
  uppercase?: boolean;
}

function SimpleTextField({
  control,
  fieldPath,
  label,
  required,
  placeholder,
  disabled,
  className,
  hideLabel,
  autoComplete,
  invalid,
  format,
  blurFormat,
  maxLength,
  uppercase,
}: SimpleTextFieldProperties) {
  const uid = useId();
  const inputId = `${uid}-input`;
  const { field, fieldState } = useController({ control, name: fieldPath });
  const isInvalid = invalid || Boolean(fieldState.error);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    field.onChange(format ? applyDigitFormatWithCaret(event, format) : event.target.value);
  }

  function handleBlur() {
    const current = field.value == null ? "" : String(field.value);
    let next = blurFormat ? blurFormat(current) : current;
    if (uppercase) {
      next = next.toUpperCase();
    }
    if (next !== current) {
      field.onChange(next);
    }
    field.onBlur();
  }

  const input = (
    <Input
      id={inputId}
      type="text"
      value={field.value == null ? "" : String(field.value)}
      onChange={format ? handleChange : field.onChange}
      onBlur={handleBlur}
      ref={field.ref}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      maxLength={maxLength}
      aria-required={required}
      aria-invalid={isInvalid || undefined}
      aria-label={hideLabel ? label : undefined}
      autoComplete={autoComplete}
    />
  );

  if (hideLabel) {
    return input;
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
        {required && <RequiredMarker />}
      </label>
      {input}
      <FormMessage error={fieldState.error} className="mt-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleDateField — a labelled date picker wired to RHF via useController
// ---------------------------------------------------------------------------
interface SimpleDateFieldProperties {
  control: Control<SignersInput>;
  fieldPath: FieldPath<SignersInput>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Mirrors SimpleTextField, but renders the uikit date picker. The form value
 * stays ISO (`YYYY-MM-DD`) — the calculator compares Date of Birth against the
 * hire and pay dates with plain string operators, so the stored shape has to
 * keep sorting lexicographically.
 */
function SimpleDateField({ control, fieldPath, label, required, disabled, className }: SimpleDateFieldProperties) {
  const uid = useId();
  const inputId = `${uid}-input`;
  const { field } = useController({ control, name: fieldPath });

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
        {required && <RequiredMarker />}
      </label>
      <DateInput
        id={inputId}
        value={field.value == null ? "" : String(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        disabled={disabled}
        className={className}
        aria-required={required}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleSelectField — a labelled select wired to RHF via useController
// ---------------------------------------------------------------------------
interface SimpleSelectFieldProperties {
  control: Control<SignersInput>;
  fieldPath: FieldPath<SignersInput>;
  label: string;
  required?: boolean;
  options: ReadonlyArray<{ value: string; label: string }>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Hide the visible label (label is exposed via aria-label instead). Used by grouped fields like Name. */
  hideLabel?: boolean;
}

function SimpleSelectField({
  control,
  fieldPath,
  label,
  required,
  options,
  disabled,
  placeholder,
  className,
  hideLabel,
}: SimpleSelectFieldProperties) {
  const uid = useId();
  const selectId = `${uid}-select`;
  const { field, fieldState } = useController({ control, name: fieldPath });
  // Radix Select ignores the enclosing disabled <fieldset>; fold the lock in
  // alongside any caller-supplied `disabled` (e.g. mailing "same as current").
  const readOnly = useFormReadOnly();
  const isDisabled = disabled || readOnly;
  const isInvalid = Boolean(fieldState.error);

  const currentValue = field.value == null ? "" : String(field.value);
  // Use shared surfacing helper: surfaces an out-of-options value and computes
  // the remount key so Radix Select can display it from the first render.
  const { resolvedOptions, selectKey: surfacedKey } = getSurfacedSelect(currentValue, options);
  // A disabled Radix Select never lets the analyst open it, so it never gets
  // the chance to register the selected item's display text — a value set
  // programmatically while disabled (e.g. useMirrorAddress hydrating mailing
  // fields while "Same as current" is checked) renders a blank trigger even
  // though the underlying value is correct. Forcing a remount whenever a
  // disabled select's value changes re-runs that registration. Left out of
  // the enabled case so a normal interactive selection doesn't lose focus.
  const selectKey = isDisabled ? `${surfacedKey}-disabled-${currentValue}` : surfacedKey;

  // Radix Select requires a non-empty string value; map "" to NONE_SENTINEL
  // so the placeholder renders and the field controller receives "" on change.
  const radixValue = currentValue === "" ? NONE_SENTINEL : currentValue;

  function handleValueChange(v: string) {
    field.onChange(v === NONE_SENTINEL ? "" : v);
  }

  const select = (
    <Select key={selectKey} value={radixValue} onValueChange={handleValueChange} disabled={isDisabled}>
      <SelectTrigger
        id={selectId}
        className={className}
        aria-label={label}
        aria-invalid={isInvalid || undefined}
        disabled={isDisabled}
      >
        <SelectValue placeholder={placeholder ?? label} />
      </SelectTrigger>
      <SelectContent>
        {resolvedOptions.map((opt) => (
          <SelectItem
            key={opt.value === "" ? NONE_SENTINEL : opt.value}
            value={opt.value === "" ? NONE_SENTINEL : opt.value}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (hideLabel) {
    return select;
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={selectId} className="text-xs font-medium">
        {label}
        {required && <RequiredMarker />}
      </label>
      {select}
      <FormMessage error={fieldState.error} className="mt-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddressFields — reusable address group (Street No / Street Name / Apt + Zip / City / State)
// ---------------------------------------------------------------------------
interface AddressFieldsProperties {
  control: Control<SignersInput>;
  streetNumberPath: FieldPath<SignersInput>;
  streetNamePath: FieldPath<SignersInput>;
  aptPath: FieldPath<SignersInput>;
  zipPath: FieldPath<SignersInput>;
  cityPath: FieldPath<SignersInput>;
  statePath: FieldPath<SignersInput>;
  disabled?: boolean;
}

function AddressFields({
  control,
  streetNumberPath,
  streetNamePath,
  aptPath,
  zipPath,
  cityPath,
  statePath,
  disabled,
}: AddressFieldsProperties) {
  const { t } = useTranslation();

  // Suppress autofill while the block is disabled (e.g. mailing when "Same as
  // current" is on — the physical autofill + mirror effect already covers that).
  const { status: zipStatus, invalid: zipInvalid } = useZipAutofill({
    zipPath,
    cityPath,
    statePath,
    enabled: !disabled,
  });

  return (
    <div className="flex flex-col gap-2">
      {/* Address: single label, placeholder-only Street No. (2) | Street Name (8) | Apt. # (2) */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium">
          {t("review.customer.residence.address")}
          <RequiredMarker />
        </span>
        <div className="@sm:grid-cols-12 grid grid-cols-1 items-start gap-x-3 gap-y-2">
          <div className="@sm:col-span-2">
            <SimpleTextField
              control={control}
              fieldPath={streetNumberPath}
              label={t("review.customer.residence.streetNumber")}
              placeholder={t("review.customer.residence.streetNumber")}
              required
              disabled={disabled}
              hideLabel
              className="w-full"
              maxLength={ADDRESS_STREET_NUMBER_MAX_LENGTH}
              uppercase
            />
          </div>
          <div className="@sm:col-span-8">
            <SimpleTextField
              control={control}
              fieldPath={streetNamePath}
              label={t("review.customer.residence.streetName")}
              placeholder={t("review.customer.residence.streetName")}
              required
              disabled={disabled}
              hideLabel
              className="w-full"
              maxLength={ADDRESS_STREET_NAME_MAX_LENGTH}
              uppercase
            />
          </div>
          <div className="@sm:col-span-2">
            <SimpleTextField
              control={control}
              fieldPath={aptPath}
              label={t("review.customer.residence.apt")}
              placeholder={t("review.customer.residence.apt")}
              disabled={disabled}
              hideLabel
              className="w-full"
              maxLength={ADDRESS_APARTMENT_NUMBER_MAX_LENGTH}
              uppercase
            />
          </div>
        </div>
      </div>

      {/* Zip | City | State */}
      <div className="@sm:grid-cols-12 grid grid-cols-1 items-start gap-x-3 gap-y-2">
        <div className="@sm:col-span-6 @lg:col-span-3">
          <SimpleTextField
            control={control}
            fieldPath={zipPath}
            label={t("review.customer.residence.zip")}
            required
            disabled={disabled}
            invalid={zipInvalid}
            format={formatZip}
            maxLength={ZIP_PASTE_MAX_LENGTH}
          />
          <ZipLookupMessage status={zipStatus} />
        </div>
        <div className="@sm:col-span-6 @lg:col-span-5">
          <SimpleSelectField
            control={control}
            fieldPath={cityPath}
            label={t("review.customer.residence.city")}
            options={CITY_OPTIONS}
            required
            disabled={disabled}
          />
        </div>
        <div className="@sm:col-span-12 @lg:col-span-4">
          <SimpleSelectField
            control={control}
            fieldPath={statePath}
            label={t("review.customer.residence.state")}
            options={US_STATE_OPTIONS}
            required
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResidenceDetailsFields — Residence Owned By + At Residence Since (OR-25320)
//
// Both describe the CURRENT residence (the BFF `current` Address slot's
// `residenceOwnerTypeId` / `residingSince`), so they render once per applicant,
// below Mailing Address. Required-ness is backend-enforced like the rest of
// this form — `RequiredMarker` is visual only, and the year's error surfaces
// from the BFF's violations (Signers.violations.ts).
//
// Hiding the landlord name/phone/address block when "Self or Spouse" is
// selected is deliberately out of scope (those inputs don't exist in workbench
// yet); the selected value is still persisted so that follow-up can key off it
// with no schema change.
// ---------------------------------------------------------------------------
interface ResidenceDetailsFieldsProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
}

/**
 * Strips every non-digit character and clamps to the 4-digit year length.
 * @param {string} value - The raw typed/pasted value.
 * @returns {string} Up to 4 digits.
 */
function formatResidingSinceYear(value: string): string {
  return value.replaceAll(/\D/g, "").slice(0, RESIDING_SINCE_YEAR_MAX_LENGTH);
}

function ResidenceDetailsFields({ control, personPath }: ResidenceDetailsFieldsProperties) {
  const { t } = useTranslation();
  const { fieldState: yearFieldState } = useController({
    control,
    name: getCustomerFieldPath(personPath, "residingSinceYear"),
  });

  return (
    <div className="@sm:flex-row @sm:items-start flex flex-col gap-3">
      <div className="@sm:w-48 w-full">
        <SimpleSelectField
          control={control}
          fieldPath={getCustomerFieldPath(personPath, "residenceOwnerType")}
          label={t("review.customer.residence.residenceOwnedBy")}
          options={RESIDENCE_OWNER_TYPE_OPTIONS}
          required
          className="w-full"
        />
      </div>

      {/* At Residence Since: single label over the Month | Year pair */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium">
          {t("review.customer.residence.atResidenceSince")}
          <RequiredMarker />
        </span>
        <div className="flex items-start gap-2">
          <SimpleSelectField
            control={control}
            fieldPath={getCustomerFieldPath(personPath, "residingSinceMonth")}
            label={t("review.customer.residence.atResidenceSinceMonth")}
            options={SHORT_MONTH_OPTIONS}
            hideLabel
            className="w-24"
          />
          <SimpleTextField
            control={control}
            fieldPath={getCustomerFieldPath(personPath, "residingSinceYear")}
            label={t("review.customer.residence.atResidenceSinceYear")}
            placeholder={t("review.customer.residence.yearPlaceholder")}
            format={formatResidingSinceYear}
            blurFormat={expandYearText}
            maxLength={RESIDING_SINCE_YEAR_MAX_LENGTH}
            required
            hideLabel
            className="w-20"
          />
        </div>
        {/* `hideLabel` suppresses SimpleTextField's own FormMessage, so the
            year's backend error is rendered here, under the grouped pair. */}
        <FormMessage error={yearFieldState.error} className="mt-0" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CustomerInformation
//
// Per-person collapsible section with three static sub-sections:
//   A. Personal Details
//   B. Residence Information (Physical + Mailing with "Same as current" mirror)
//   C. Contact Information (phones field array + email with "Email Not Available")
//
// Collapse state is block-local (UI concern only — mirrors BankInformation pattern).
// ---------------------------------------------------------------------------
export function CustomerInformation({
  control,
  personPath,
  hasChecklistWarning = false,
  applicantSsnType,
  identityReadOnly,
  residenceSectionRef,
  approvalId,
  applicantId,
}: CustomerInformationProperties) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  // Unique id per mount prevents aria-controls collisions when both driver and
  // secondSigner columns are rendered in the same document.
  const uid = useId();
  const contentId = `${personPath}-customer-info-${uid}-content`;

  // clearErrors/setError/getFieldState back the live duplicate-phone and
  // email-format checks below, plus dropping a stale email error when "Email
  // Not Available" is checked (RHF onBlur mode won't re-validate on the
  // checkbox toggle alone).
  const { clearErrors, setError, getFieldState } = useFormContext<SignersInput>();

  // ---------------------------------------------------------------------------
  // Phones field array — scoped inside CustomerInformation (no external consumer)
  // ---------------------------------------------------------------------------
  const phonesName = `${personPath}.customerInfo.phones` as
    `driver.customerInfo.phones` | `secondSigner.customerInfo.phones`;
  const { fields: phoneFields, append, remove } = useFieldArray({ control, name: phonesName });
  const atPhoneCap = phoneFields.length >= MAX_PHONES_PER_PERSON;

  // Two phone rows on the SAME applicant sharing a complete number — a
  // single-applicant check the backend's Validations model could express,
  // but with no confirmed backend equivalent (PR #254 review) it's enforced
  // live here instead, mirroring Signers.tsx's SSN-duplicate check. Only
  // clears an error this effect itself set (type "duplicate-phone") so it
  // never clobbers a backend-set error on the same field.
  const watchedPhones = useWatch({ control, name: phonesName });
  useEffect(() => {
    const duplicateIndexes = findDuplicatePhoneIndexes(watchedPhones ?? []);
    for (const [index] of (watchedPhones ?? []).entries()) {
      const numberPath = `${personPath}.customerInfo.phones.${index}.number` as FieldPath<SignersInput>;
      if (duplicateIndexes.has(index)) {
        setError(numberPath, { type: "duplicate-phone", message: DUPLICATE_PHONE_MESSAGE });
      } else if (getFieldState(numberPath).error?.type === "duplicate-phone") {
        clearErrors(numberPath);
      }
    }
  }, [watchedPhones, personPath, setError, clearErrors, getFieldState]);

  // ---------------------------------------------------------------------------
  // "Same as current" — delegate to useMirrorAddress hook
  // ---------------------------------------------------------------------------
  const { mailingSameAsCurrent, onSameAsCurrentChange } = useMirrorAddress({ control, personPath });

  // ---------------------------------------------------------------------------
  // "Email Not Available"
  // ---------------------------------------------------------------------------
  const emailNotAvailable = useWatch({
    control,
    name: getCustomerFieldPath(personPath, "emailNotAvailable"),
  });
  const { field: emailNotAvailableField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "emailNotAvailable"),
  });
  const emailPath = getCustomerFieldPath(personPath, "emailAddress");
  const { field: emailField, fieldState: emailFieldState } = useController({
    control,
    name: emailPath,
  });

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  function handleEmailNotAvailableChange(checked: boolean | "indeterminate") {
    const isChecked = checked === true;
    emailNotAvailableField.onChange(isChecked);
    if (isChecked) {
      // Clear the email value AND any stale validation error, then disable the
      // input (disabled is driven by emailNotAvailable in the render).
      emailField.onChange("");
      clearErrors(emailPath);
    }
  }

  function handleAddPhone() {
    if (!atPhoneCap) {
      append(makeEmptyPhone());
    }
  }

  function handleEmailBlur() {
    const current = emailField.value == null ? "" : String(emailField.value);
    const trimmed = current.trim();
    if (trimmed !== current) {
      emailField.onChange(trimmed);
    }
    emailField.onBlur();

    // Format check — a single-applicant rule the backend's Validations model
    // could express, but with no confirmed backend equivalent (PR #254
    // review) it's enforced live here. Only sets/clears an error of its own
    // type ("email-format") so it never clobbers a backend-set error.
    if (trimmed !== "" && !isValidEmailFormat(trimmed)) {
      setError(emailPath, { type: "email-format", message: EMAIL_INVALID_MESSAGE });
    } else if (getFieldState(emailPath).error?.type === "email-format") {
      clearErrors(emailPath);
    }
  }

  // ---------------------------------------------------------------------------
  // Checkbox uid for a11y label association
  // ---------------------------------------------------------------------------
  const sameAsCurrentId = `${uid}-same-as-current`;
  const emailNotAvailableId = `${uid}-email-not-available`;
  const emailInputId = `${uid}-email`;
  const emailErrorId = `${uid}-email-error`;

  return (
    <div ref={residenceSectionRef}>
      <SubsectionHeader
        title={t("review.customer.heading")}
        isOpen={isOpen}
        onToggle={handleToggle}
        contentId={contentId}
        hasError={hasChecklistWarning}
      />

      {/* Section content */}
      {isOpen && (
        <div id={contentId} className="flex flex-col gap-4 px-3 pb-4 pt-3">
          {/* ----------------------------------------------------------------
              A. Personal Details
          ---------------------------------------------------------------- */}
          <section aria-label={t("review.customer.sections.personalDetails")}>
            <h4 className="text-foreground mb-2 text-xs font-semibold tracking-wide">
              {t("review.customer.sections.personalDetails")}
            </h4>
            {/* SSN | Date of Birth (5) + full-width (20) Name row —
                Driver License and Hold Coupon Book fields removed, leaving a gap
                after Date of Birth. SSN's own column is sized "auto" (fits its
                fixed-width split input exactly) instead of a fr-share, so no
                dead space is left before the gap-x-3 gutter — a fr-share here
                either overflows the SSN input (too small) or leaves a large
                blank gap before DOB (too big).

                No-SSN/ITIN applicants have no SSN field at all, so the grid
                swaps to a uniform 20-column fr template instead — otherwise
                Date of Birth (now the first item) auto-places into the narrow
                "auto" track sized for SSN, shrinking it.

                SSN/ITIN, Date of Birth and the Name row use `identityReadOnly`
                instead of the general form read-only lock — see that prop's
                doc comment (matrix §4.3 + status-plan Phase 1 `disablePreApp`
                override). */}
            <div
              className={cn(
                "grid grid-cols-1 items-start gap-x-3 gap-y-3",
                applicantSsnType === APPLICANT_SSN_TYPES.NO_ITIN_SSN
                  ? "@sm:grid-cols-[repeat(20,minmax(0,1fr))]"
                  : "@sm:grid-cols-[auto_repeat(19,minmax(0,1fr))]"
              )}
            >
              {applicantSsnType !== APPLICANT_SSN_TYPES.NO_ITIN_SSN && (
                <div>
                  <SsnField control={control} personPath={personPath} disabled={identityReadOnly} />
                </div>
              )}
              <div className="@sm:col-span-5 @sm:min-w-40">
                <SimpleDateField
                  control={control}
                  fieldPath={getCustomerFieldPath(personPath, "dateOfBirth")}
                  label={t("review.customer.personal.dateOfBirth")}
                  required
                  disabled={identityReadOnly}
                />
              </div>

              {/* Name (full width): single label, placeholder-only First | MI | Last | Suffix */}
              <div className="@sm:col-span-20">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium">
                    {t("review.customer.personal.name")}
                    <RequiredMarker />
                  </span>
                  {/* `@sm:flex-wrap` matches the established convention for this
                      row shape (see IncomeBlock's Gross Monthly Pay | Hire Date |
                      Show Calculator row) — narrow columns wrap Suffix onto its
                      own line instead of forcing it to shrink illegibly or
                      overflow into the neighboring signer's column (SVRS-55520). */}
                  <div className="@sm:flex-row @sm:flex-wrap @sm:items-start flex flex-col gap-2">
                    <div className="flex-1">
                      <SimpleTextField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "firstName")}
                        label={t("review.customer.personal.firstName")}
                        placeholder={t("review.customer.personal.firstName")}
                        required
                        disabled={identityReadOnly}
                        hideLabel
                        className="w-full"
                      />
                    </div>
                    <div className="@sm:w-12 w-full">
                      <SimpleTextField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "middleInitial")}
                        label={t("review.customer.personal.middleInitial")}
                        disabled={identityReadOnly}
                        hideLabel
                        className="w-full"
                      />
                    </div>
                    <div className="flex-1">
                      <SimpleTextField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "lastName")}
                        label={t("review.customer.personal.lastName")}
                        placeholder={t("review.customer.personal.lastName")}
                        required
                        disabled={identityReadOnly}
                        hideLabel
                        className="w-full"
                      />
                    </div>
                    <div className="@sm:w-24 w-full">
                      <SimpleSelectField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "suffix")}
                        label={t("review.customer.personal.suffix")}
                        options={SUFFIX_OPTIONS}
                        placeholder={t("review.customer.personal.suffix")}
                        disabled={identityReadOnly}
                        hideLabel
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ----------------------------------------------------------------
              B. Residence Information
          ---------------------------------------------------------------- */}
          <section aria-label={t("review.customer.sections.residenceInformation")}>
            <h4 className="text-foreground mb-2 text-xs font-semibold tracking-wide">
              {t("review.customer.sections.residenceInformation")}
            </h4>
            <div className="flex flex-col gap-3">
              {/* Physical Address */}
              <fieldset>
                <legend className="text-foreground mb-1 text-xs font-medium">
                  {t("review.customer.residence.physicalAddress")}
                </legend>
                <AddressFields
                  control={control}
                  streetNumberPath={getCustomerFieldPath(personPath, "physicalStreetNumber")}
                  streetNamePath={getCustomerFieldPath(personPath, "physicalStreetName")}
                  aptPath={getCustomerFieldPath(personPath, "physicalApartmentNumber")}
                  zipPath={getCustomerFieldPath(personPath, "physicalZip")}
                  cityPath={getCustomerFieldPath(personPath, "physicalCity")}
                  statePath={getCustomerFieldPath(personPath, "physicalState")}
                />
              </fieldset>

              {/* Mailing Address */}
              <fieldset>
                {/* Mailing Address label with the "Same as current" checkbox inline */}
                <legend className="text-foreground mb-2 flex flex-wrap items-center gap-4 text-xs font-medium">
                  {t("review.customer.residence.mailingAddress")}
                  <span className="flex items-center gap-2">
                    <Checkbox
                      id={sameAsCurrentId}
                      checked={Boolean(mailingSameAsCurrent)}
                      onCheckedChange={onSameAsCurrentChange}
                    />
                    <label htmlFor={sameAsCurrentId} className="cursor-pointer text-xs font-normal">
                      {t("review.customer.residence.sameAsCurrent")}
                    </label>
                  </span>
                </legend>
                <AddressFields
                  control={control}
                  streetNumberPath={getCustomerFieldPath(personPath, "mailingStreetNumber")}
                  streetNamePath={getCustomerFieldPath(personPath, "mailingStreetName")}
                  aptPath={getCustomerFieldPath(personPath, "mailingApartmentNumber")}
                  zipPath={getCustomerFieldPath(personPath, "mailingZip")}
                  cityPath={getCustomerFieldPath(personPath, "mailingCity")}
                  statePath={getCustomerFieldPath(personPath, "mailingState")}
                  disabled={Boolean(mailingSameAsCurrent)}
                />
              </fieldset>

              {/* Residence Owned By + At Residence Since (current residence) */}
              <fieldset>
                <legend className="text-foreground mb-2 text-xs font-medium">
                  {t("review.customer.residence.residenceDetails")}
                </legend>
                <ResidenceDetailsFields control={control} personPath={personPath} />
              </fieldset>
            </div>
          </section>

          {/* ----------------------------------------------------------------
              C. Contact Information
          ---------------------------------------------------------------- */}
          <section aria-label={t("review.customer.sections.contactInformation")}>
            <h4 className="text-foreground mb-2 text-xs font-semibold tracking-wide">
              {t("review.customer.sections.contactInformation")}
            </h4>
            {/* Single stacked column: phone rows, then Add button, then email below */}
            <div className="flex flex-col gap-4">
              <div className="flex w-full flex-col gap-3">
                {phoneFields.map((field, index) => (
                  <PhoneRow
                    key={field.id}
                    control={control}
                    personPath={personPath}
                    index={index}
                    onRemove={index > 1 ? () => remove(index) : undefined}
                    // identityReadOnly folds in pre-approval apps, where CAPS2 also hides the button.
                    showCallScript={index === 0 && !identityReadOnly}
                    approvalId={approvalId}
                    applicantId={applicantId}
                  />
                ))}

                {/* Add phone number — subtle, always-underlined link right-aligned to the section edge */}
                {!atPhoneCap && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={handleAddPhone}
                    className="text-muted-foreground text-2xs h-auto w-fit gap-1 self-end p-0 font-normal underline"
                  >
                    <PlusIcon className="size-3" aria-hidden="true" />
                    {t("review.customer.contact.addPhone")}
                  </Button>
                )}
              </div>

              {/* Email — horizontal (label-left), with Email Not Available beneath */}
              <div className="flex flex-col gap-2">
                <div className="@sm:flex-row @sm:items-center @sm:gap-3 flex flex-col gap-1">
                  <label
                    htmlFor={emailInputId}
                    className="@sm:w-[var(--signer-label-width)] @sm:shrink-0 text-xs font-medium"
                  >
                    {t("review.customer.contact.email")}
                    <RequiredMarker />
                  </label>
                  <div className="flex flex-col gap-1">
                    {/* Width aligns the input's right edge to the phone Type select:
                        number block (w-12 + w-12 + w-14 + 4×gap-1 + 2×dash w-2 = 11.5rem)
                        + gap-2 (0.5rem) + Type select w-32 (8rem) = 20rem → w-80. */}
                    <Input
                      id={emailInputId}
                      type="text"
                      value={emailField.value == null ? "" : String(emailField.value)}
                      onChange={emailField.onChange}
                      onBlur={handleEmailBlur}
                      ref={emailField.ref}
                      disabled={Boolean(emailNotAvailable)}
                      className="@sm:w-80 w-full"
                      aria-required
                      aria-invalid={Boolean(emailFieldState.error) || undefined}
                      aria-describedby={emailFieldState.error ? emailErrorId : undefined}
                    />
                    <FormMessage error={emailFieldState.error} id={emailErrorId} className="mt-0" />
                  </div>
                </div>

                {/* Email Not Available — below the email row, aligned under the input */}
                <div className="@sm:flex-row @sm:gap-3 flex flex-col gap-1">
                  <span
                    className="@sm:block @sm:w-[var(--signer-label-width)] @sm:shrink-0 hidden"
                    aria-hidden="true"
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={emailNotAvailableId}
                      checked={Boolean(emailNotAvailable)}
                      onCheckedChange={handleEmailNotAvailableChange}
                    />
                    <label htmlFor={emailNotAvailableId} className="cursor-pointer text-xs font-normal">
                      {t("review.customer.contact.emailNotAvailable")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
